import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import dayjs from 'dayjs';
import {
    Camera,
    ChevronLeft,
    ChevronRight,
    GitCompareArrows,
    Images,
    RefreshCw,
    RotateCcw,
    ShieldCheck,
} from 'lucide-react';
import { Col, Row } from '../../lib/utils';
import ImageViewerModal from '../modal/ImageViewerModal';
import { BodyPhotoImage, MemberBodyPhoto } from '@/features/bodyPhoto/types';

type Props = {
    photos: MemberBodyPhoto[];
    isLoading?: boolean;
    isRefreshing?: boolean;
    isError?: boolean;
    onRetry?: () => void;
    coachNameById?: Record<string, string>;
};

type ComparisonSide = 'base' | 'target';

const getCapturedAt = (photo: MemberBodyPhoto) =>
    dayjs(photo.capturedAt || photo.recordDate);

const getCapturedAtValue = (photo: MemberBodyPhoto) => {
    const capturedAt = getCapturedAt(photo);
    return capturedAt.isValid() ? capturedAt.valueOf() : 0;
};

const comparePhotosOldestFirst = (a: MemberBodyPhoto, b: MemberBodyPhoto) => {
    const capturedAtDifference = getCapturedAtValue(a) - getCapturedAtValue(b);
    if (capturedAtDifference !== 0) return capturedAtDifference;

    const createdAtDifference =
        dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf();
    if (Number.isFinite(createdAtDifference) && createdAtDifference !== 0)
        return createdAtDifference;
    return a.id.localeCompare(b.id);
};

const formatCapturedAt = (photo: MemberBodyPhoto) => {
    const capturedAt = getCapturedAt(photo);
    if (!capturedAt.isValid()) return photo.recordDate || '-';
    return capturedAt.format('YYYY년 M월 D일');
};

const formatComparisonOption = (photo: MemberBodyPhoto) => {
    const capturedAt = getCapturedAt(photo);
    const dateLabel = capturedAt.isValid()
        ? capturedAt.format('YYYY. M. D. HH:mm')
        : photo.recordDate || '-';
    const authorLabel = photo.uploadedByRole === 'MEMBER' ? '회원' : '강사';
    return `${dateLabel} · ${authorLabel} · 사진 ${photo.images.length}장`;
};

const getAuthorLabel = (
    photo: MemberBodyPhoto,
    coachNameById: Record<string, string>,
) => {
    if (photo.uploadedByRole === 'MEMBER') return '회원 작성';
    const coachName = coachNameById[photo.uploadedById];
    return coachName ? `${coachName} 강사 작성` : '강사 작성';
};

const getBrowserDisplayUrl = (image: BodyPhotoImage) =>
    image.mimeType === 'image/heic' || image.mimeType === 'image/heif'
        ? image.thumbnailUrl || image.url
        : image.url || image.thumbnailUrl;

const getPreviewUrl = (image: BodyPhotoImage) =>
    image.thumbnailUrl || getBrowserDisplayUrl(image);

const badgeStyle = (
    backgroundColor: string,
    color: string,
): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: 24,
    padding: '3px 8px',
    borderRadius: 999,
    backgroundColor,
    color,
    fontSize: 11,
    fontWeight: 700,
    whiteSpace: 'nowrap',
});

const railButtonStyle = (disabled: boolean): React.CSSProperties => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 30,
    height: 30,
    flexShrink: 0,
    padding: 0,
    borderRadius: 999,
    border: '1px solid #CBD5E1',
    background: '#FFFFFF',
    color: disabled ? '#CBD5E1' : '#2563EB',
    boxShadow: disabled ? 'none' : '0 2px 7px rgba(15, 23, 42, 0.1)',
    cursor: disabled ? 'default' : 'pointer',
});

const LoadingState = () => (
    <Col style={{ gap: 12 }} aria-label="눈바디 기록을 불러오는 중">
        {[0, 1].map((item) => (
            <div
                key={item}
                className="animate-pulse"
                style={{
                    height: 220,
                    borderRadius: 16,
                    background: '#F1F5F9',
                    border: '1px solid #E5E7EB',
                }}
            />
        ))}
    </Col>
);

const EmptyState = ({
    onRetry,
    isRefreshing = false,
}: {
    onRetry?: () => void;
    isRefreshing?: boolean;
}) => (
    <Col
        style={{
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 320,
            padding: '64px 24px',
            borderRadius: 18,
            border: '1px dashed #D1D5DB',
            background: 'linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)',
            textAlign: 'center',
        }}
    >
        <Camera
            size={44}
            color="#94A3B8"
            strokeWidth={1.6}
            style={{ marginBottom: 16 }}
        />
        <span
            style={{
                fontSize: 19,
                fontWeight: 800,
                color: '#111827',
                marginBottom: 8,
            }}
        >
            회원이 공개한 눈바디 기록이 없습니다
        </span>
        <span
            style={{
                maxWidth: 430,
                fontSize: 15,
                lineHeight: 1.6,
                color: '#6B7280',
            }}
        >
            회원이 현재 연결된 강사에게 공유한 기록만 관리자 화면에 표시됩니다.
        </span>
        {onRetry && (
            <button
                type="button"
                onClick={onRetry}
                disabled={isRefreshing}
                aria-live="polite"
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    marginTop: 20,
                    padding: '9px 14px',
                    borderRadius: 9,
                    border: '1px solid #D1D5DB',
                    background: '#FFFFFF',
                    color: '#374151',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: isRefreshing ? 'wait' : 'pointer',
                    opacity: isRefreshing ? 0.7 : 1,
                }}
            >
                <RefreshCw
                    size={14}
                    className={isRefreshing ? 'animate-spin' : undefined}
                />{' '}
                {isRefreshing ? '확인 중...' : '다시 확인'}
            </button>
        )}
    </Col>
);

const ErrorState = ({ onRetry }: { onRetry?: () => void }) => (
    <Col
        role="alert"
        style={{
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 320,
            padding: 48,
            borderRadius: 18,
            border: '1px solid #FECACA',
            background: '#FFF7F7',
            textAlign: 'center',
        }}
    >
        <span style={{ fontSize: 18, fontWeight: 800, color: '#991B1B' }}>
            눈바디 기록을 불러오지 못했습니다
        </span>
        <span style={{ marginTop: 8, fontSize: 14, color: '#B91C1C' }}>
            잠시 후 다시 시도해주세요.
        </span>
        {onRetry && (
            <button
                type="button"
                onClick={onRetry}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 7,
                    marginTop: 20,
                    padding: '9px 14px',
                    borderRadius: 9,
                    border: '1px solid #FCA5A5',
                    background: '#FFFFFF',
                    color: '#B91C1C',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                }}
            >
                <RefreshCw size={14} /> 다시 시도
            </button>
        )}
    </Col>
);

const PhotoThumbnail = ({
    image,
    index,
    onClick,
}: {
    image: BodyPhotoImage;
    index: number;
    onClick: () => void;
}) => {
    const [hasError, setHasError] = useState(false);

    return (
        <button
            type="button"
            onClick={onClick}
            aria-label={`눈바디 사진 ${index + 1} 크게 보기`}
            style={{
                position: 'relative',
                width: 92,
                height: 122,
                flexShrink: 0,
                padding: 0,
                overflow: 'hidden',
                scrollSnapAlign: 'start',
                borderRadius: 10,
                border: '1px solid #E2E8F0',
                background: '#F8FAFC',
                cursor: hasError ? 'default' : 'zoom-in',
            }}
            disabled={hasError}
        >
            {hasError ? (
                <Col
                    style={{
                        height: '100%',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 6,
                        color: '#94A3B8',
                    }}
                >
                    <Images size={21} strokeWidth={1.5} />
                    <span style={{ fontSize: 10 }}>불러오기 실패</span>
                </Col>
            ) : (
                <>
                    <img
                        src={getPreviewUrl(image)}
                        alt={`눈바디 사진 ${index + 1}`}
                        loading="lazy"
                        draggable={false}
                        onError={() => setHasError(true)}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                    <span
                        aria-hidden="true"
                        style={{
                            position: 'absolute',
                            right: 6,
                            bottom: 6,
                            minWidth: 20,
                            padding: '3px 6px',
                            borderRadius: 999,
                            background: 'rgba(15, 23, 42, 0.72)',
                            color: '#FFFFFF',
                            fontSize: 10,
                            fontWeight: 700,
                        }}
                    >
                        {index + 1}
                    </span>
                </>
            )}
        </button>
    );
};

const CompareColumn = ({
    photo,
    side,
    availablePhotos,
    otherPhotoId,
    marker,
    coachNameById,
    onSelect,
    onOpenViewer,
}: {
    photo: MemberBodyPhoto;
    side: ComparisonSide;
    availablePhotos: MemberBodyPhoto[];
    otherPhotoId: string;
    marker?: string;
    coachNameById: Record<string, string>;
    onSelect: (photoId: string) => void;
    onOpenViewer: (photo: MemberBodyPhoto, index: number) => void;
}) => {
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const thumbnailRailRef = useRef<HTMLDivElement | null>(null);
    const thumbnailButtonRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const activeImage = photo.images[activeImageIndex] || photo.images[0];
    const sideLabel = side === 'base' ? '기준 기록' : '비교 기록';

    useEffect(() => {
        setActiveImageIndex(0);
        if (thumbnailRailRef.current) thumbnailRailRef.current.scrollLeft = 0;
    }, [photo.id]);

    useEffect(() => {
        const rail = thumbnailRailRef.current;
        const thumbnail = thumbnailButtonRefs.current[activeImageIndex];
        if (!rail || !thumbnail) return;

        const railRect = rail.getBoundingClientRect();
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const thumbnailCenterInRail =
            thumbnailRect.left -
            railRect.left +
            rail.scrollLeft +
            thumbnailRect.width / 2;
        const centeredScrollLeft = thumbnailCenterInRail - rail.clientWidth / 2;
        const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
        const nextScrollLeft = Math.min(
            Math.max(0, centeredScrollLeft),
            maxScrollLeft,
        );

        if (typeof rail.scrollTo === 'function') {
            rail.scrollTo({ left: nextScrollLeft, behavior: 'smooth' });
        } else {
            rail.scrollLeft = nextScrollLeft;
        }
    }, [activeImageIndex, photo.id]);

    const moveToImage = (nextIndex: number) => {
        setActiveImageIndex(
            Math.min(Math.max(nextIndex, 0), photo.images.length - 1),
        );
    };

    return (
        <Col
            data-testid={`comparison-${side}`}
            style={{
                minWidth: 0,
                gap: 12,
                padding: 14,
                borderRadius: 14,
                border: '1px solid #DCE6F2',
                background: '#FFFFFF',
            }}
        >
            <Row
                style={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                }}
            >
                <span
                    style={{ fontSize: 12, fontWeight: 800, color: '#2563EB' }}
                >
                    {sideLabel}
                </span>
                {marker && (
                    <span style={badgeStyle('#EFF6FF', '#1D4ED8')}>
                        {marker}
                    </span>
                )}
            </Row>

            <select
                aria-label={`${sideLabel} 선택`}
                value={photo.id}
                onChange={(event) => onSelect(event.target.value)}
                style={{
                    width: '100%',
                    height: 36,
                    padding: '0 34px 0 10px',
                    borderRadius: 9,
                    border: '1px solid #CBD5E1',
                    background: '#FFFFFF',
                    color: '#0F172A',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                }}
            >
                {availablePhotos.map((option) => (
                    <option
                        key={option.id}
                        value={option.id}
                        disabled={option.id === otherPhotoId}
                    >
                        {formatComparisonOption(option)}
                    </option>
                ))}
            </select>

            <Row
                style={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                    color: '#64748B',
                }}
            >
                <span style={{ fontSize: 12, fontWeight: 600 }}>
                    {getAuthorLabel(photo, coachNameById)}
                </span>
                <span style={{ fontSize: 11 }}>
                    사진 {photo.images.length}장
                </span>
            </Row>

            {activeImage && (
                <button
                    type="button"
                    onClick={() => onOpenViewer(photo, activeImageIndex)}
                    aria-label={`${sideLabel} 눈바디 사진 크게 보기`}
                    style={{
                        width: '100%',
                        maxWidth: 250,
                        aspectRatio: '3 / 4',
                        alignSelf: 'center',
                        padding: 0,
                        borderRadius: 12,
                        overflow: 'hidden',
                        border: '1px solid #DCE3EC',
                        background: '#0F172A',
                        cursor: 'zoom-in',
                    }}
                >
                    <img
                        src={getPreviewUrl(activeImage)}
                        alt={`${sideLabel} 눈바디`}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'contain',
                            display: 'block',
                        }}
                    />
                </button>
            )}

            {photo.images.length > 1 && (
                <Col
                    style={{
                        alignSelf: 'center',
                        alignItems: 'center',
                        gap: 5,
                        maxWidth: '100%',
                    }}
                >
                    <Row
                        style={{
                            width: '100%',
                            maxWidth: 322,
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                        }}
                    >
                        <button
                            type="button"
                            aria-label={`${sideLabel} 이전 사진 보기`}
                            title="이전 사진"
                            disabled={activeImageIndex === 0}
                            onClick={() => moveToImage(activeImageIndex - 1)}
                            style={railButtonStyle(activeImageIndex === 0)}
                        >
                            <ChevronLeft size={17} />
                        </button>

                        <Row
                            ref={thumbnailRailRef}
                            aria-label={`${sideLabel} 사진 목록`}
                            style={{
                                width: '100%',
                                maxWidth: 250,
                                minWidth: 0,
                                gap: 7,
                                overflowX: 'auto',
                                scrollSnapType: 'x proximity',
                                scrollBehavior: 'smooth',
                                padding: '2px 1px',
                            }}
                        >
                            {photo.images.map((image, index) => (
                                <button
                                    key={image.imageId}
                                    ref={(element) => {
                                        thumbnailButtonRefs.current[index] =
                                            element;
                                    }}
                                    type="button"
                                    aria-label={`${sideLabel} 사진 ${index + 1} 선택`}
                                    aria-pressed={activeImageIndex === index}
                                    onClick={() => moveToImage(index)}
                                    style={{
                                        width: 42,
                                        height: 56,
                                        flexShrink: 0,
                                        padding: 0,
                                        overflow: 'hidden',
                                        scrollSnapAlign: 'center',
                                        borderRadius: 7,
                                        border:
                                            activeImageIndex === index
                                                ? '2px solid #2563EB'
                                                : '1px solid #CBD5E1',
                                        background: '#F8FAFC',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <img
                                        src={getPreviewUrl(image)}
                                        alt=""
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            objectFit: 'contain',
                                            display: 'block',
                                        }}
                                    />
                                </button>
                            ))}
                        </Row>

                        <button
                            type="button"
                            aria-label={`${sideLabel} 다음 사진 보기`}
                            title="다음 사진"
                            disabled={
                                activeImageIndex === photo.images.length - 1
                            }
                            onClick={() => moveToImage(activeImageIndex + 1)}
                            style={railButtonStyle(
                                activeImageIndex === photo.images.length - 1,
                            )}
                        >
                            <ChevronRight size={17} />
                        </button>
                    </Row>
                    <span
                        aria-live="polite"
                        style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#64748B',
                        }}
                    >
                        {activeImageIndex + 1} / {photo.images.length}
                    </span>
                </Col>
            )}

            {photo.note && (
                <span
                    title={photo.note}
                    style={{
                        color: '#64748B',
                        fontSize: 12,
                        lineHeight: 1.5,
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                    }}
                >
                    {photo.note}
                </span>
            )}
        </Col>
    );
};

const getComparisonMarker = (
    photo: MemberBodyPhoto,
    photos: MemberBodyPhoto[],
) => {
    const isFirst = photo.id === photos[0]?.id;
    const isLatest = photo.id === photos[photos.length - 1]?.id;
    if (isFirst && isLatest) return '첫·최신 기록';
    if (isFirst) return '첫 기록';
    if (isLatest) return '최신 기록';
    return undefined;
};

const ComparisonPanel = ({
    photos,
    basePhoto,
    targetPhoto,
    coachNameById,
    isDefaultSelection,
    onSelect,
    onReset,
    onOpenViewer,
}: {
    photos: MemberBodyPhoto[];
    basePhoto: MemberBodyPhoto;
    targetPhoto: MemberBodyPhoto;
    coachNameById: Record<string, string>;
    isDefaultSelection: boolean;
    onSelect: (side: ComparisonSide, photoId: string) => void;
    onReset: () => void;
    onOpenViewer: (photo: MemberBodyPhoto, index: number) => void;
}) => (
    <section
        aria-label="눈바디 변화 비교"
        style={{
            padding: 18,
            borderRadius: 16,
            border: '1px solid #BFDBFE',
            background: 'linear-gradient(180deg, #EFF6FF 0%, #F8FBFF 100%)',
        }}
    >
        <Row
            style={{
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 14,
                marginBottom: 14,
            }}
        >
            <Col style={{ minWidth: 0, gap: 4 }}>
                <Row style={{ alignItems: 'center', gap: 8 }}>
                    <GitCompareArrows size={19} color="#2563EB" />
                    <span
                        style={{
                            fontSize: 17,
                            fontWeight: 800,
                            color: '#1E3A8A',
                        }}
                    >
                        눈바디 변화 비교
                    </span>
                </Row>
                <span
                    style={{ fontSize: 12, lineHeight: 1.5, color: '#64748B' }}
                >
                    첫 기록과 최신 기록을 기본으로 보여드려요. 각 날짜를 눌러
                    다른 기록으로 바꿀 수 있습니다.
                </span>
            </Col>
            <button
                type="button"
                onClick={onReset}
                disabled={isDefaultSelection}
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    flexShrink: 0,
                    minHeight: 32,
                    padding: '6px 9px',
                    borderRadius: 8,
                    border: '1px solid #CBD5E1',
                    background: '#FFFFFF',
                    color: '#475569',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: isDefaultSelection ? 'default' : 'pointer',
                    opacity: isDefaultSelection ? 0.55 : 1,
                }}
            >
                <RotateCcw size={13} /> 첫·최신 기록으로
            </button>
        </Row>

        <div
            style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: 12,
            }}
        >
            <CompareColumn
                photo={basePhoto}
                side="base"
                availablePhotos={photos}
                otherPhotoId={targetPhoto.id}
                marker={getComparisonMarker(basePhoto, photos)}
                coachNameById={coachNameById}
                onSelect={(photoId) => onSelect('base', photoId)}
                onOpenViewer={onOpenViewer}
            />
            <CompareColumn
                photo={targetPhoto}
                side="target"
                availablePhotos={photos}
                otherPhotoId={basePhoto.id}
                marker={getComparisonMarker(targetPhoto, photos)}
                coachNameById={coachNameById}
                onSelect={(photoId) => onSelect('target', photoId)}
                onOpenViewer={onOpenViewer}
            />
        </div>
    </section>
);

type RailScrollState = {
    hasOverflow: boolean;
    canScrollLeft: boolean;
    canScrollRight: boolean;
};

const initialRailScrollState: RailScrollState = {
    hasOverflow: false,
    canScrollLeft: false,
    canScrollRight: false,
};

const HistoryPhotoRail = ({
    photo,
    onOpenViewer,
}: {
    photo: MemberBodyPhoto;
    onOpenViewer: (photo: MemberBodyPhoto, index: number) => void;
}) => {
    const railRef = useRef<HTMLDivElement | null>(null);
    const [scrollState, setScrollState] = useState<RailScrollState>(
        initialRailScrollState,
    );
    const recordLabel = formatComparisonOption(photo);

    const updateScrollState = useCallback(() => {
        const rail = railRef.current;
        if (!rail) return;

        const edgeTolerance = 2;
        const hasOverflow = rail.scrollWidth > rail.clientWidth + edgeTolerance;
        const nextState = {
            hasOverflow,
            canScrollLeft: hasOverflow && rail.scrollLeft > edgeTolerance,
            canScrollRight:
                hasOverflow &&
                rail.scrollLeft + rail.clientWidth <
                    rail.scrollWidth - edgeTolerance,
        };

        setScrollState((current) =>
            current.hasOverflow === nextState.hasOverflow &&
            current.canScrollLeft === nextState.canScrollLeft &&
            current.canScrollRight === nextState.canScrollRight
                ? current
                : nextState,
        );
    }, []);

    useEffect(() => {
        const rail = railRef.current;
        if (!rail) return;

        const frameId = window.requestAnimationFrame(updateScrollState);
        rail.addEventListener('scroll', updateScrollState, { passive: true });
        window.addEventListener('resize', updateScrollState);

        const resizeObserver =
            typeof ResizeObserver === 'undefined'
                ? null
                : new ResizeObserver(updateScrollState);
        resizeObserver?.observe(rail);

        return () => {
            window.cancelAnimationFrame(frameId);
            rail.removeEventListener('scroll', updateScrollState);
            window.removeEventListener('resize', updateScrollState);
            resizeObserver?.disconnect();
        };
    }, [photo.images.length, updateScrollState]);

    const scrollRail = (direction: -1 | 1) => {
        const rail = railRef.current;
        if (!rail) return;

        const scrollAmount = Math.max(100, rail.clientWidth * 0.8);
        if (typeof rail.scrollBy === 'function') {
            rail.scrollBy({
                left: direction * scrollAmount,
                behavior: 'smooth',
            });
        } else {
            rail.scrollLeft += direction * scrollAmount;
        }
        window.requestAnimationFrame(updateScrollState);
    };

    return (
        <Row
            style={{
                flex: '3 1 420px',
                minWidth: 0,
                alignItems: 'center',
                gap: 6,
            }}
        >
            {scrollState.hasOverflow && (
                <button
                    type="button"
                    aria-label={`${recordLabel} 사진 목록 이전으로`}
                    title="이전 사진 보기"
                    disabled={!scrollState.canScrollLeft}
                    onClick={() => scrollRail(-1)}
                    style={railButtonStyle(!scrollState.canScrollLeft)}
                >
                    <ChevronLeft size={17} />
                </button>
            )}

            <Row
                ref={railRef}
                data-testid={`history-photo-rail-${photo.id}`}
                aria-label={`${recordLabel} 사진 목록`}
                style={{
                    flex: '1 1 auto',
                    minWidth: 0,
                    gap: 8,
                    overflowX: 'auto',
                    scrollSnapType: 'x mandatory',
                    scrollBehavior: 'smooth',
                    paddingBottom: 2,
                }}
            >
                {photo.images.map((image, index) => (
                    <PhotoThumbnail
                        key={image.imageId}
                        image={image}
                        index={index}
                        onClick={() => onOpenViewer(photo, index)}
                    />
                ))}
            </Row>

            {scrollState.hasOverflow && (
                <button
                    type="button"
                    aria-label={`${recordLabel} 사진 목록 다음으로`}
                    title="다음 사진 보기"
                    disabled={!scrollState.canScrollRight}
                    onClick={() => scrollRail(1)}
                    style={railButtonStyle(!scrollState.canScrollRight)}
                >
                    <ChevronRight size={17} />
                </button>
            )}
        </Row>
    );
};

const HistoryRecord = ({
    photo,
    coachNameById,
    onOpenViewer,
}: {
    photo: MemberBodyPhoto;
    coachNameById: Record<string, string>;
    onOpenViewer: (photo: MemberBodyPhoto, index: number) => void;
}) => (
    <article
        style={{
            padding: '14px 16px',
            borderRadius: 14,
            border: '1px solid #E5E7EB',
            background: '#FFFFFF',
            boxShadow: '0 2px 8px rgba(15, 23, 42, 0.035)',
        }}
    >
        <Row style={{ alignItems: 'stretch', gap: 16, flexWrap: 'wrap' }}>
            <Col
                style={{
                    flex: '1 1 220px',
                    minWidth: 0,
                    maxWidth: 340,
                    gap: 7,
                }}
            >
                <Row style={{ alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                    <span
                        style={{
                            fontSize: 16,
                            fontWeight: 800,
                            color: '#111827',
                        }}
                    >
                        {formatCapturedAt(photo)}
                    </span>
                    <span
                        style={badgeStyle(
                            photo.uploadedByRole === 'MEMBER'
                                ? '#F0FDF4'
                                : '#FFF7ED',
                            photo.uploadedByRole === 'MEMBER'
                                ? '#15803D'
                                : '#C2410C',
                        )}
                    >
                        {getAuthorLabel(photo, coachNameById)}
                    </span>
                </Row>
                <span style={{ fontSize: 11, color: '#94A3B8' }}>
                    사진 {photo.images.length}장
                </span>
                {photo.note && (
                    <span
                        title={photo.note}
                        style={{
                            color: '#475569',
                            fontSize: 12,
                            lineHeight: 1.55,
                            overflow: 'hidden',
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                        }}
                    >
                        {photo.note}
                    </span>
                )}
            </Col>

            <HistoryPhotoRail photo={photo} onOpenViewer={onOpenViewer} />
        </Row>
    </article>
);

const MemberBodyPhotoTab = ({
    photos,
    isLoading = false,
    isRefreshing = false,
    isError = false,
    onRetry,
    coachNameById = {},
}: Props) => {
    const [baseOverrideId, setBaseOverrideId] = useState<string | null>(null);
    const [targetOverrideId, setTargetOverrideId] = useState<string | null>(
        null,
    );
    const [viewerState, setViewerState] = useState<{
        photoId: string;
        initialIndex: number;
    } | null>(null);

    const chronologicalPhotos = useMemo(
        () => [...photos].sort(comparePhotosOldestFirst),
        [photos],
    );
    const historyPhotos = useMemo(
        () => [...chronologicalPhotos].reverse(),
        [chronologicalPhotos],
    );
    const visiblePhotoIds = useMemo(
        () => new Set(chronologicalPhotos.map((photo) => photo.id)),
        [chronologicalPhotos],
    );

    useEffect(() => {
        if (baseOverrideId && !visiblePhotoIds.has(baseOverrideId))
            setBaseOverrideId(null);
        if (targetOverrideId && !visiblePhotoIds.has(targetOverrideId))
            setTargetOverrideId(null);
    }, [baseOverrideId, targetOverrideId, visiblePhotoIds]);

    const comparisonPhotos = useMemo(() => {
        if (chronologicalPhotos.length < 2) return null;

        const firstPhoto = chronologicalPhotos[0];
        const latestPhoto = chronologicalPhotos[chronologicalPhotos.length - 1];
        const baseOverridePhoto = baseOverrideId
            ? chronologicalPhotos.find((photo) => photo.id === baseOverrideId)
            : undefined;
        const targetOverridePhoto = targetOverrideId
            ? chronologicalPhotos.find((photo) => photo.id === targetOverrideId)
            : undefined;

        let basePhoto = baseOverridePhoto || firstPhoto;
        let targetPhoto = targetOverridePhoto || latestPhoto;

        if (basePhoto.id === targetPhoto.id) {
            if (baseOverridePhoto && !targetOverridePhoto) {
                targetPhoto =
                    [...chronologicalPhotos]
                        .reverse()
                        .find((photo) => photo.id !== basePhoto.id) ||
                    latestPhoto;
            } else {
                basePhoto =
                    chronologicalPhotos.find(
                        (photo) => photo.id !== targetPhoto.id,
                    ) || firstPhoto;
            }
        }

        return { basePhoto, targetPhoto };
    }, [baseOverrideId, chronologicalPhotos, targetOverrideId]);

    const viewerPhoto = useMemo(
        () =>
            viewerState
                ? photos.find((photo) => photo.id === viewerState.photoId)
                : undefined,
        [photos, viewerState],
    );

    useEffect(() => {
        if (viewerState && !viewerPhoto) setViewerState(null);
    }, [viewerPhoto, viewerState]);

    const openViewer = (photo: MemberBodyPhoto, initialIndex: number) => {
        setViewerState({ photoId: photo.id, initialIndex });
    };

    const selectComparisonPhoto = (side: ComparisonSide, photoId: string) => {
        const firstPhotoId = chronologicalPhotos[0]?.id;
        const latestPhotoId =
            chronologicalPhotos[chronologicalPhotos.length - 1]?.id;

        if (side === 'base') {
            setBaseOverrideId(photoId === firstPhotoId ? null : photoId);
            return;
        }
        setTargetOverrideId(photoId === latestPhotoId ? null : photoId);
    };

    if (isLoading) return <LoadingState />;
    if (isError) return <ErrorState onRetry={onRetry} />;
    if (!photos.length)
        return <EmptyState onRetry={onRetry} isRefreshing={isRefreshing} />;

    return (
        <Col style={{ gap: 16 }}>
            <Row
                style={{
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 14,
                    padding: '12px 14px',
                    borderRadius: 12,
                    border: '1px solid #DBEAFE',
                    background: '#EFF6FF',
                }}
            >
                <Row style={{ alignItems: 'center', gap: 9, minWidth: 0 }}>
                    <ShieldCheck
                        size={18}
                        color="#2563EB"
                        style={{ flexShrink: 0 }}
                    />
                    <span
                        style={{
                            fontSize: 12,
                            lineHeight: 1.5,
                            color: '#1E40AF',
                            fontWeight: 600,
                        }}
                    >
                        현재 연결된 강사가 볼 수 있도록 공개된 기록만
                        표시됩니다. 관리자는 조회만 할 수 있습니다.
                    </span>
                </Row>
                <span style={badgeStyle('#DBEAFE', '#1D4ED8')}>
                    공개 기록 {photos.length}건
                </span>
            </Row>

            {comparisonPhotos ? (
                <ComparisonPanel
                    photos={chronologicalPhotos}
                    basePhoto={comparisonPhotos.basePhoto}
                    targetPhoto={comparisonPhotos.targetPhoto}
                    coachNameById={coachNameById}
                    isDefaultSelection={!baseOverrideId && !targetOverrideId}
                    onSelect={selectComparisonPhoto}
                    onReset={() => {
                        setBaseOverrideId(null);
                        setTargetOverrideId(null);
                    }}
                    onOpenViewer={openViewer}
                />
            ) : (
                <Row
                    aria-label="눈바디 비교 안내"
                    style={{
                        alignItems: 'center',
                        gap: 8,
                        minHeight: 42,
                        padding: '10px 13px',
                        borderRadius: 11,
                        background: '#F8FAFC',
                        color: '#64748B',
                        fontSize: 12,
                        fontWeight: 600,
                    }}
                >
                    <GitCompareArrows size={16} /> 비교하려면 공개된 눈바디
                    기록이 2개 이상 필요합니다.
                </Row>
            )}

            <section aria-label="눈바디 전체 기록">
                <Row
                    style={{
                        alignItems: 'flex-end',
                        justifyContent: 'space-between',
                        gap: 12,
                        marginBottom: 10,
                    }}
                >
                    <Col style={{ gap: 3 }}>
                        <span
                            style={{
                                fontSize: 17,
                                fontWeight: 800,
                                color: '#111827',
                            }}
                        >
                            전체 기록
                        </span>
                        <span style={{ fontSize: 11, color: '#94A3B8' }}>
                            최근 기록부터 순서대로 보여드려요.
                        </span>
                    </Col>
                    <span
                        style={{
                            fontSize: 11,
                            color: '#64748B',
                            fontWeight: 700,
                        }}
                    >
                        총 {photos.length}건
                    </span>
                </Row>
                <Col style={{ gap: 10 }}>
                    {historyPhotos.map((photo) => (
                        <HistoryRecord
                            key={photo.id}
                            photo={photo}
                            coachNameById={coachNameById}
                            onOpenViewer={openViewer}
                        />
                    ))}
                </Col>
            </section>

            {viewerState && viewerPhoto && (
                <ImageViewerModal
                    images={viewerPhoto.images.map(getBrowserDisplayUrl)}
                    initialIndex={viewerState.initialIndex}
                    isOpen={true}
                    onClose={() => setViewerState(null)}
                />
            )}
        </Col>
    );
};

export default MemberBodyPhotoTab;
